import React, { useEffect, useState, useContext, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { UserInterface } from '../types';
import useApi from '../api';
import toastConfig from '../config/Toast';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useOutsideAlerter } from '../useOutsideAlerter';
import { TeamType } from '../types';
import { get } from 'https';
import _, { set } from 'lodash';
import { Toast } from 'react-toastify/dist/components';

const SearchUsers = ({ onSearch }: { onSearch: any }) => {
  const [input, setInput] = useState('');

  // Debounced search function
  const debouncedSearch = useRef(_.debounce(onSearch, 300)).current;

  useEffect(() => {
    if (input) {
      debouncedSearch(input);
    }
  }, [input, debouncedSearch]);

  return (
    <div className='mx-auto rounded-md flex flex-col'>
    <div className="relative flex w-1/2 h-12 rounded-lg bg-white border">
      <input
        className="h-full w-full border-0 text-sm text-gray-700 pr-2 focus:outline-none focus:ring-1 rounded-lg focus:ring-blue-200 focus:border-transparent"
        type="text"
        id="search"
        placeholder="Add users.."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        autoComplete="off"
      />

    </div>
  </div>
  );
};


const Team: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserInterface[]>([]);
  const [team, setTeam] = useState<TeamType>();
  const [teamColor, setTeamColor] = useState<string>('');
  const [searchResults, setSearchResults] = useState<UserInterface[]>([]);
  const [newTeamModal, setNewTeamModal] = useState(false);
  const navigate = useNavigate(); // Create a navigate function
  const { id } = useParams();
  const { get, getStream, post, postStream, put, patch, remove } = useApi();
  const [creator, setCreator] = useState<UserInterface>();

  const usersRef = useRef(users);
  usersRef.current = users;

  useEffect(() => {
    getTeam();
  }, [id]);

  const getTeam = async () => {
    try {
      setLoading(true);

      const response = await get(`/teams/${id}`);

      console.log(response);
      setUsers(response.data.users);
      setTeam(response.data);

    } catch (error) {
      console.error('Error fetching team:', error);
      toast.error(`Error fetching team`, toastConfig);
      navigate('/teams');

    }
    setLoading(false);
  };

  const addUser = async (userId: number) => {
    try {
        setLoading(true);
        const response = await post(`/teams/${id}/users/`, { user_id: userId });
        // Assuming the response includes the user data
        const addedUser = response.data;
        setUsers((prevUsers) => [...prevUsers, addedUser]);
        setSearchResults([]);
        toast.success(`User added to team`, toastConfig);
    } catch (error) {
        console.error('Error adding user to team:', error);
        toast.error(`Error adding user to team`, toastConfig);
    } finally {
        setLoading(false);
    }
};


  const removeUser = async (userId: number) => {

    try {
      setLoading(true);
      const response = await remove(`/teams/${id}/users/${userId}`);
      setUsers(users.filter((user) => user.id !== userId));
      console.log(response.data);
      toast.success(`User removed from team`, toastConfig);
    } catch (error) {
      console.error('Error deleting useration:', error);
      toast.error(`Error removing user from team`, toastConfig);
    }
    setLoading(false);
  };

  const handleSearch = async (search: string) => {
    try {
      const response = await get(`/users/?search=${search}`);
      console.log(response);
      console.log(users)
      filterSearchResults(response.data, usersRef.current);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const filterSearchResults = (searchResults: UserInterface[], currentUsers: UserInterface[]) => {
    // Results should not incude members of the team
    setSearchResults(searchResults.filter((user) => !currentUsers.some((u) => u.id === user.id)));
  };

  function getTeamInitials(teamName: string) {
    const words = teamName.trim().split(/\s+/);
    if (words.length >= 2) {
      // Return the first letter of the first two words
      return words[0].charAt(0) + words[1].charAt(0);
    } else {
      // Return the first letter of the team name followed by 'T'
      return teamName.charAt(0) + 'T';
    }
  }

  function getRandomColor() {
    const colors = ["green", "blue"];
    const randomIndex = Math.floor(Math.random() * colors.length);
    return colors[randomIndex];
  }

  useEffect(() => {
    if (team) {
      setTeamColor(getRandomColor());
    }
  }, [team]);

  // get team creator
  useEffect(() => {
    const getCreator = async (createdBy: string) => {
      try {
        const response = await get(`/users/${createdBy}`);
        console.log(response);
        setCreator(response.data);
      } catch (error) {
        console.error('Error fetching team creator:', error);
      }
    };
    if (team) {
      getCreator(team.created_by);
    }
  }, [team]);

  const searchResultsRef = useRef(null);

  useOutsideAlerter(searchResultsRef, () => setSearchResults([]));

  return (
    team ? (
      <div className="grow">

        <ToastContainer />
        <div className="mt-16 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin">
          <div className="border-b p-6 bg-gray-100 flex flex-row">
            {teamColor && (
              <div className={`w-14 h-14 mr-4 flex items-center justify-center rounded-full bg-${teamColor}-500 text-white`}>
                <span className="text-2xl font-semibold">{getTeamInitials(team.name)}</span>
              </div>
            )}
            <div className="flex items-center">
              <h2 className="text-3xl font-semibold leading-7 text-gray-600">{`${team.name} ${team.name.toLowerCase().includes('team') ? "" : "Team"}` || "Documentation Details"}</h2>
            </div>
          </div>
          {creator && (
        <div className="mr-8">
        <div className="mt-8 ml-8 block text-sm font-medium leading-6 text-gray-500">
        Team Creator
        </div>
          <div className="text-base text-gray-900 font-normal flex items-center group grow ml-8 mt-2 pb-8 border-b">
            <div className={`w-8 h-8 mr-4 flex items-center justify-center rounded-full bg-blue-500 text-white`}>
                <span className="text-md font-semibold">{creator.email[0]}</span>
            </div>
            <span className="flex-1 whitespace-pre-wrap">{creator.email}</span>
        </div>
        </div>
      )}
          <div className="pb-8 pt-8 pl-8 pr-8">
              <SearchUsers onSearch={handleSearch} />
              {searchResults.length > 0 && (
              <div ref={searchResultsRef} className="border w-1/2 bg-gray-50 shadow-md rounded-b-md">
                {searchResults.map((user) => (
                  <div key={user.id} 
                    className="p-2 hover:bg-gray-100 text-sm"
                    onClick={() => addUser(user.id)}>
                    <div className="flex flex-row items-center">
                      <div className={`w-8 h-8 mr-2 flex items-center justify-center rounded-full bg-${teamColor}-500 text-white`}>
                        <span className="text-md font-semibold">{user.username[0]}</span>
                      </div>
                      <div>
                        <div>
                          {user.username}
                        </div>
                        <div className="text-gray-400">
                          {user.username}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
      )}
      {users.length > 0 && (
                    <div className="flex flex-col mt-8">
                    <div className="-m-1.5 overflow-x-auto">
                      <div className="p-1.5 min-w-full inline-block align-middle">
                        <div className="border rounded-lg shadow overflow-hidden dark:border-gray-700 dark:shadow-gray-900">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                                  Username
                                </th>
                                <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                                  Email
                                </th>
                                <th scope="col" className="px-6 py-3 text-end text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700">
                              {users.length > 0 &&
                                users.map((user: UserInterface) => (
                                  <tr key={user.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800 dark:text-gray-200">
                                      {user.username}
                                    </td>
                                    <td className="px-6 py-4 whitespace-pre-line text-sm text-gray-800 dark:text-gray-200 truncate">
                                      {user.email}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                                      <button onClick={() => removeUser(user.id)} type="button" className="inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:pointer-events-none dark:text-blue-500 dark:hover:text-blue-400 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600">Remove</button>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
      )}

          </div>
        </div>
      </div>
    ) : (
      <></>
    )
  );
};

export default Team;